import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Tooltip, ButtonBase } from '@mui/material';
import { styled } from '@mui/material/styles';
import { chatBotService } from '@services/ChatBot/ChatBotService';
import SupportPanel, { SUPPORT_CONTACT, SUPPORT_GRADIENT } from './SupportPanel';

const FONT = "'Inter Tight', Inter, -apple-system, 'Segoe UI', sans-serif";

const WELCOME = `Xin chào! 👋 Tôi là **Trợ lý kỹ thuật GSWM**.

Tôi có thể giúp bạn:
- 📊 Tra cứu *tiêu thụ theo ngày* (chi nhánh) và *theo kỳ cước* (khách hàng)
- ⚠️ Tra cứu *cảnh báo* theo mã khách hàng
- 📡 Giải đáp kỹ thuật cơ bản: offline, sóng NB-IoT, pin, các loại cảnh báo

Chọn một gợi ý bên dưới hoặc nhập câu hỏi của bạn.`;

const SUGGESTIONS = [
  'Tiêu thụ hôm nay của HP7',
  'Đồng hồ offline thường do đâu?',
  'Sóng -115 dBm có sao không?',
  'Cảnh báo tháo rời là gì?'
];

let messageSeq = 0;
const makeMessage = (text, isUser, extra = {}) => ({
  id: `m${Date.now()}-${(messageSeq += 1)}`,
  text,
  isUser,
  timestamp: new Date(),
  ...extra
});

const formatTime = (date) =>
  date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });

const Svg = ({ children, size = 18, strokeWidth = 2 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true">
    {children}
  </svg>
);

const HeadsetIcon = ({ size = 18 }) => (
  <Svg size={size}>
    <path d="M3 11h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-5Zm0 0a9 9 0 1 1 18 0m0 0v5a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3Z" />
    <path d="M21 16v2a4 4 0 0 1-4 4h-5" />
  </Svg>
);

const BotIcon = ({ size = 18 }) => (
  <Svg size={size}>
    <path d="M12 8V4H8" />
    <rect x="4" y="8" width="16" height="12" rx="2" />
    <path d="M2 14h2M20 14h2M15 13v2M9 13v2" />
  </Svg>
);

/* ---------------- Chat window ---------------- */

const Window = styled('section')(() => ({
  position: 'fixed',
  right: 24,
  bottom: 24,
  width: 400,
  height: 'min(640px, calc(100vh - 48px))',
  display: 'flex',
  flexDirection: 'column',
  zIndex: 9999,
  overflow: 'hidden',
  borderRadius: 18,
  fontFamily: FONT,
  color: 'var(--text)',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  boxShadow: '0 28px 56px -20px rgba(15, 23, 42, 0.4), 0 6px 16px rgba(15, 23, 42, 0.08)',
  animation: 'chatIn 0.2s ease-out',
  '@keyframes chatIn': {
    from: { opacity: 0, transform: 'translateY(12px) scale(0.98)' },
    to: { opacity: 1, transform: 'none' }
  },
  '@media (max-width: 600px)': {
    right: 8,
    left: 8,
    bottom: 8,
    width: 'auto',
    height: 'calc(100vh - 16px)'
  }
}));

const Header = styled('header')(() => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 12px 14px 16px',
  color: '#ffffff',
  background: SUPPORT_GRADIENT,
  '& .h-avatar': {
    position: 'relative',
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: 12,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.18)',
    boxShadow: 'inset 0 0 0 1px rgba(255, 255, 255, 0.25)'
  },
  '& .h-dot': {
    position: 'absolute',
    right: -2,
    bottom: -2,
    width: 11,
    height: 11,
    borderRadius: '50%',
    background: '#22c55e',
    boxShadow: '0 0 0 2px #0284c7'
  },
  '& .h-text': { flex: 1, minWidth: 0, lineHeight: 1.3 },
  '& .h-text strong': { display: 'block', fontSize: 15.5, fontWeight: 700 },
  '& .h-text span': { fontSize: 12, opacity: 0.85 },
  '& .h-btn': {
    width: 32,
    height: 32,
    border: 'none',
    borderRadius: 9,
    cursor: 'pointer',
    color: '#ffffff',
    background: 'transparent',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.15s ease',
    '&:hover': { background: 'rgba(255, 255, 255, 0.16)' }
  }
}));

const Messages = styled('div')(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '18px 14px 8px',
  display: 'flex',
  flexDirection: 'column',
  gap: 14,
  background: 'var(--surface-2)'
}));

const noIsUser = { shouldForwardProp: (prop) => prop !== 'isUser' };

const Row = styled(
  'div',
  noIsUser
)(({ isUser }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  gap: 8,
  alignSelf: isUser ? 'flex-end' : 'flex-start',
  maxWidth: '88%',
  '& .bot-avatar': {
    width: 28,
    height: 28,
    flexShrink: 0,
    marginBottom: 18,
    borderRadius: 9,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    background: SUPPORT_GRADIENT
  },
  '& .stack': {
    display: 'flex',
    flexDirection: 'column',
    alignItems: isUser ? 'flex-end' : 'flex-start',
    minWidth: 0
  },
  '& .time': {
    marginTop: 4,
    padding: '0 4px',
    fontSize: 11,
    color: 'var(--subtle)'
  }
}));

const Bubble = styled(
  'div',
  noIsUser
)(({ isUser }) => ({
  padding: '10px 14px',
  fontSize: 14,
  lineHeight: 1.6,
  wordBreak: 'break-word',
  borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
  color: isUser ? '#ffffff' : 'var(--text)',
  background: isUser ? SUPPORT_GRADIENT : 'var(--surface)',
  border: isUser ? 'none' : '1px solid var(--border)',
  boxShadow: isUser ? '0 6px 14px -8px rgba(2, 132, 199, 0.7)' : '0 1px 2px rgba(15,23,42,0.04)',
  '& p': {
    margin: '0.45em 0',
    '&:first-of-type': { marginTop: 0 },
    '&:last-child': { marginBottom: 0 }
  },
  '& strong': { fontWeight: 650, color: isUser ? '#ffffff' : 'var(--text)' },
  '& em': {
    fontStyle: 'normal',
    fontWeight: 500,
    color: isUser ? 'rgba(255,255,255,0.9)' : 'var(--muted)'
  },
  '& ul, & ol': { margin: '0.45em 0', paddingLeft: '1.25em' },
  '& li': { margin: '0.2em 0' },
  '& li::marker': { color: isUser ? '#ffffff' : '#0284c7' },
  '& h1, & h2, & h3, & h4': { margin: '0.6em 0 0.3em', fontSize: 14.5, fontWeight: 700 },
  '& hr': { border: 'none', borderTop: '1px solid var(--border)', margin: '0.7em 0' },
  '& code': {
    padding: '1px 6px',
    borderRadius: 6,
    fontSize: '0.86em',
    fontFamily: 'var(--font-mono, ui-monospace, Menlo, Consolas, monospace)',
    background: isUser ? 'rgba(255,255,255,0.2)' : 'var(--surface-3)'
  },
  '& pre': {
    margin: '0.5em 0',
    padding: '8px 12px',
    overflow: 'auto',
    borderRadius: 8,
    background: isUser ? 'rgba(0,0,0,0.2)' : 'var(--surface-3)'
  },
  '& table': { borderCollapse: 'collapse', margin: '0.5em 0', fontSize: 13 },
  '& th, & td': { border: '1px solid var(--border)', padding: '4px 8px' },
  '& a': { color: isUser ? '#ffffff' : '#0284c7', textDecoration: 'underline' },
  '& .data-note': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 8,
    padding: '2px 8px',
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 600,
    color: '#0284c7',
    background: 'var(--primary-soft)'
  }
}));

const Typing = styled('div')(() => ({
  display: 'inline-flex',
  gap: 4,
  padding: '14px 16px',
  borderRadius: '16px 16px 16px 4px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  '& span': {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#0284c7',
    opacity: 0.35,
    animation: 'typingDot 1.2s infinite ease-in-out'
  },
  '& span:nth-of-type(2)': { animationDelay: '0.15s' },
  '& span:nth-of-type(3)': { animationDelay: '0.3s' },
  '@keyframes typingDot': {
    '0%, 80%, 100%': { opacity: 0.35, transform: 'translateY(0)' },
    '40%': { opacity: 1, transform: 'translateY(-4px)' }
  }
}));

const Suggestions = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  paddingLeft: 36,
  '& button': {
    padding: '7px 12px',
    borderRadius: 999,
    cursor: 'pointer',
    font: 'inherit',
    fontSize: 12.5,
    fontWeight: 500,
    color: '#0284c7',
    background: 'var(--surface)',
    border: '1px solid color-mix(in srgb, #0284c7 30%, var(--border))',
    transition: 'background 0.15s ease, border-color 0.15s ease',
    '&:hover': { background: 'var(--primary-soft)', borderColor: '#0284c7' }
  }
}));

const Composer = styled('div')(() => ({
  flexShrink: 0,
  padding: '10px 12px 12px',
  borderTop: '1px solid var(--border)',
  background: 'var(--surface)',
  '& .box': {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
    padding: '6px 6px 6px 14px',
    borderRadius: 14,
    border: '1px solid var(--border)',
    background: 'var(--surface-2)',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    '&:focus-within': {
      borderColor: '#0284c7',
      boxShadow: '0 0 0 3px rgba(2, 132, 199, 0.14)'
    }
  },
  '& textarea': {
    flex: 1,
    minHeight: 24,
    maxHeight: 120,
    padding: '7px 0',
    border: 'none',
    outline: 'none',
    resize: 'none',
    background: 'transparent',
    color: 'var(--text)',
    font: 'inherit',
    fontSize: 14,
    lineHeight: 1.45,
    '&::placeholder': { color: 'var(--subtle)' }
  },
  '& .send': {
    width: 36,
    height: 36,
    flexShrink: 0,
    border: 'none',
    borderRadius: 10,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    background: SUPPORT_GRADIENT,
    transition: 'filter 0.15s ease, opacity 0.15s ease',
    '&:hover:not(:disabled)': { filter: 'brightness(1.08)' },
    '&:disabled': { cursor: 'not-allowed', opacity: 0.4 }
  },
  '& .note': {
    marginTop: 8,
    fontSize: 11,
    textAlign: 'center',
    color: 'var(--subtle)'
  },
  '& .note a': { color: 'var(--muted)', fontWeight: 600, textDecoration: 'none' }
}));

/* ---------------- Launcher ---------------- */

// Pill-shaped "technical support" launcher (opens the support popup)
const FloatingButton = styled(ButtonBase)(() => ({
  position: 'fixed',
  bottom: 39,
  right: 24,
  height: 48,
  padding: '0 20px 0 10px',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 10,
  borderRadius: 999,
  color: '#ffffff',
  fontFamily: FONT,
  fontSize: 14.5,
  fontWeight: 600,
  letterSpacing: '-0.005em',
  background: SUPPORT_GRADIENT,
  boxShadow: '0 10px 24px -8px rgba(2, 132, 199, 0.55), 0 2px 6px rgba(2, 132, 199, 0.25)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  zIndex: 9998,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 14px 28px -8px rgba(2, 132, 199, 0.6), 0 2px 6px rgba(2, 132, 199, 0.3)'
  },
  '&:active': { transform: 'translateY(0)' },
  '& .support-icon': {
    position: 'relative',
    width: 32,
    height: 32,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.18)'
  },
  '& .support-dot': {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 9,
    height: 9,
    borderRadius: '50%',
    background: '#22c55e',
    boxShadow: '0 0 0 2px #0284c7'
  },
  '@media (max-width: 600px)': {
    padding: '0 8px',
    '& .support-label': { display: 'none' }
  }
}));

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [messages, setMessages] = useState(() => [makeMessage(WELCOME, false)]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  useEffect(() => {
    if (!isOpen) return undefined;
    inputRef.current?.focus();
    const onKey = (e) => e.key === 'Escape' && setIsOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Grow the textarea with its content (up to the CSS max-height)
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [inputValue]);

  // textOverride lets the support popup / suggestions send without the input box
  const handleSendMessage = async (textOverride) => {
    const text = typeof textOverride === 'string' ? textOverride : inputValue;
    if (!text.trim() || isLoading) return;

    const history = messages;
    setMessages((prev) => [...prev, makeMessage(text, true)]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await chatBotService.sendMessage(text, history);
      setMessages((prev) => [
        ...prev,
        makeMessage(response.message, false, { data: response.data })
      ]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        makeMessage(
          `Xin lỗi, đã có lỗi xảy ra. Vui lòng thử lại sau hoặc liên hệ hotline **${SUPPORT_CONTACT.hotline}**.`,
          false
        )
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const resetConversation = () => {
    if (isLoading) return;
    setMessages([makeMessage(WELCOME, false)]);
    setInputValue('');
  };

  const openAssistant = () => {
    setSupportOpen(false);
    setIsOpen(true);
  };

  const handleSubmitTicket = (text) => {
    openAssistant();
    handleSendMessage(`Phiếu xử lý sự cố: ${text}`);
  };

  const showSuggestions = messages.length === 1 && !isLoading;

  return (
    <>
      {!isOpen && supportOpen && (
        <SupportPanel
          onClose={() => setSupportOpen(false)}
          onSubmitTicket={handleSubmitTicket}
          onOpenAssistant={openAssistant}
        />
      )}

      {!isOpen && (
        <Tooltip title={supportOpen ? '' : 'Hỗ trợ kỹ thuật'} placement="left">
          <FloatingButton
            className="support-fab"
            aria-label="Hỗ trợ kỹ thuật"
            aria-expanded={supportOpen}
            onClick={() => setSupportOpen((open) => !open)}>
            <span className="support-icon">
              <HeadsetIcon />
              <span className="support-dot" />
            </span>
            <span className="support-label">Hỗ trợ kỹ thuật</span>
          </FloatingButton>
        </Tooltip>
      )}

      {isOpen && (
        <Window className="support-fab" role="dialog" aria-label="Trợ lý kỹ thuật GSWM">
          <Header>
            <span className="h-avatar">
              <BotIcon size={20} />
              <span className="h-dot" />
            </span>
            <div className="h-text">
              <strong>Trợ lý kỹ thuật GSWM</strong>
              <span>Trực tuyến · AI hỗ trợ tra cứu & vận hành</span>
            </div>
            <Tooltip title="Cuộc trò chuyện mới">
              <button
                type="button"
                className="h-btn"
                aria-label="Cuộc trò chuyện mới"
                onClick={resetConversation}>
                <Svg size={17}>
                  <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                  <path d="M3 3v5h5" />
                </Svg>
              </button>
            </Tooltip>
            <button
              type="button"
              className="h-btn"
              aria-label="Đóng"
              onClick={() => setIsOpen(false)}>
              <Svg size={18}>
                <path d="M18 6 6 18" />
                <path d="m6 6 12 12" />
              </Svg>
            </button>
          </Header>

          <Messages>
            {messages.map((message) => (
              <Row key={message.id} isUser={message.isUser}>
                {!message.isUser && (
                  <span className="bot-avatar">
                    <BotIcon size={15} />
                  </span>
                )}
                <div className="stack">
                  <Bubble isUser={message.isUser}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
                    {message.data && (
                      <span className="data-note">📊 Dữ liệu tra cứu từ hệ thống</span>
                    )}
                  </Bubble>
                  <span className="time">{formatTime(message.timestamp)}</span>
                </div>
              </Row>
            ))}

            {showSuggestions && (
              <Suggestions>
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" onClick={() => handleSendMessage(s)}>
                    {s}
                  </button>
                ))}
              </Suggestions>
            )}

            {isLoading && (
              <Row isUser={false}>
                <span className="bot-avatar" style={{ marginBottom: 0 }}>
                  <BotIcon size={15} />
                </span>
                <Typing aria-label="Đang trả lời">
                  <span />
                  <span />
                  <span />
                </Typing>
              </Row>
            )}
            <div ref={messagesEndRef} />
          </Messages>

          <Composer>
            <div className="box">
              <textarea
                ref={inputRef}
                rows={1}
                placeholder="Nhập câu hỏi… (Enter để gửi, Shift+Enter xuống dòng)"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                disabled={isLoading}
                maxLength={2000}
              />
              <button
                type="button"
                className="send"
                aria-label="Gửi"
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || isLoading}>
                <Svg size={17}>
                  <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                  <path d="m21.854 2.147-10.94 10.939" />
                </Svg>
              </button>
            </div>
            <div className="note">
              AI có thể sai sót · Cần hỗ trợ gấp:{' '}
              <a href={`tel:${SUPPORT_CONTACT.hotlineTel}`}>{SUPPORT_CONTACT.hotline}</a>
            </div>
          </Composer>
        </Window>
      )}
    </>
  );
};

export default ChatBot;
